import { useCallback, useEffect, useRef, useState } from 'react';
import { listCustomerSessions } from '@/api/sessions';
import type { SessionDto } from '@/types';

/** D13: twenty per page. */
const PAGE_SIZE = 20;

export interface PastConversationsState {
  /** Every row the server has returned so far, in the server's order. The caller drops the open conversation. */
  sessions: SessionDto[];
  /** Another page exists (the server said so; it is never guessed from a full page). */
  hasMore: boolean;
  /** The FIRST page is in flight. */
  loading: boolean;
  /** The first page failed - the section says so; it never renders an empty list as "no past conversations". */
  failed: boolean;
  /** A further page is in flight. */
  loadingMore: boolean;
  /** A further page failed; the rows already shown stay, and the button is still there to try again. */
  moreFailed: boolean;
  loadMore: () => void;
  /** Ask for the first page again after it failed. */
  retry: () => void;
}

/**
 * One customer's conversations, a page at a time (the contact panel's "Past conversations", ADR-033 D7).
 *
 * <b>Free until asked.</b> Nothing is requested while `enabled` is false: the section is collapsed by default, and a
 * request per conversation an agent merely clicks past is the cost this guards. Opening it is one click and the choice
 * persists (the caller owns that flag).
 *
 * <b>Never a stale answer.</b> Every load carries a generation; a change of customer (or a close and re-open) bumps it, and
 * an answer from an older generation is dropped - the previous customer's conversations must not render under the new one.
 * A failure is a state of its own (`failed`), never an empty list: "we could not read it" and "there is none" are
 * different sentences.
 */
export function usePastConversations(customerId: string, enabled: boolean): PastConversationsState {
  const [sessions, setSessions] = useState<SessionDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  const generation = useRef(0);
  const nextPage = useRef(2);
  const busy = useRef(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    busy.current = false;
    nextPage.current = 2;
    setSessions([]);
    setHasMore(false);
    setFailed(false);
    setLoadingMore(false);
    setMoreFailed(false);
    if (!enabled || !customerId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    listCustomerSessions(customerId, 1, PAGE_SIZE)
      .then((page) => {
        if (generation.current !== mine) return;
        setSessions(page.items);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        if (generation.current !== mine) return;
        setFailed(true);
      })
      .finally(() => {
        if (generation.current === mine) setLoading(false);
      });

    return () => {
      // Invalidates this generation's answer even when nothing else changed (unmount, collapse).
      generation.current += 1;
    };
  }, [customerId, enabled, attempt]);

  const loadMore = useCallback(() => {
    if (!enabled || !customerId || !hasMore || busy.current) return;
    busy.current = true;
    const mine = generation.current;
    const page = nextPage.current;
    setLoadingMore(true);
    setMoreFailed(false);
    listCustomerSessions(customerId, page, PAGE_SIZE)
      .then((res) => {
        if (generation.current !== mine) return;
        nextPage.current = page + 1;
        setSessions((prev) => [...prev, ...res.items]);
        setHasMore(res.hasMore);
      })
      .catch(() => {
        if (generation.current !== mine) return;
        setMoreFailed(true);
      })
      .finally(() => {
        if (generation.current === mine) {
          busy.current = false;
          setLoadingMore(false);
        }
      });
  }, [enabled, customerId, hasMore]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return { sessions, hasMore, loading, failed, loadingMore, moreFailed, loadMore, retry };
}
